import { get } from 'lodash';
import Divider from 'material-ui/Divider';
import * as React from 'react';
import { Col, Grid, Row } from 'react-flexbox-grid';

import {
  AutoComplete,
  AutoCompleteField,
  Date,
  DateField,
  InlineGrid,
  InlineGridField,
  Input,
  InputField,
  NumberSequence,
  RemoteAutoComplete,
  RemoteAutoCompleteField,
  RemoteAutoCompleteTree,
  RemoteAutoCompleteTreeField,
  RemoteChipInput,
  RemoteChipInputField
} from '@components/form';

import Address, { AddressField } from '@components/form/Address/Address';
import { withNotifications } from '@components/hoc/withNotifications';
import { getConfig } from '@components/provider/configuration/_configurationActions';
import { eventsFormClassificationsGridColumns } from '@modules/events/_eventsColumns';
import { stateEnum } from '@modules/events/_eventsEnums';
import { required } from '@utils/validators';
import { compose, lifecycle, withProps } from 'recompose';

const enhance = compose<any, any>(
  withNotifications({ getConfig }),
  lifecycle<any, {}, {}>({
    async componentDidMount() {
      const { getConfigNotify } = this.props;
      try {
        const config = await getConfigNotify();
        this.setState({ config });
      } catch {
        this.setState({ config: {} });
      }
    }
  }),
  withProps(({ config }) => ({
    eventAutoCusSeq: get(config, 'extensions.event-auto-cus-seq') === 'true'
  }))
);

const formatDateTimeString = (dateTimeObject: any) => {
  let result = '';
  let index = 0;

  if (typeof dateTimeObject !== 'object') {
    return result;
  }

  while (Object.prototype.hasOwnProperty.call(dateTimeObject, index)) {
    result += dateTimeObject[index];
    index++;
  }

  return result;
};

const View: React.SFC<any> = ({ change, disabled, eventDefaultPlace, eventAutoCusSeq }) => (
  <Grid fluid={true}>
    <Row>
      <Col className="grid-system-margin" />
    </Row>
    <Row>
      <Col md={3} className="with-right-divider">
        <Row>
          <Col md={12}>
            <h4>Identifikace</h4>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <NumberSequence
              inputName={'number'}
              label={'Číslo *'}
              formName={'module-events'}
              sequenceKey={'sequenceEvent'}
              inputProps={{ disabled: true }}
              disabled={disabled}
              validate={[required]}
            />
          </Col>
          <Col md={12}>
            {eventAutoCusSeq ? (
              <NumberSequence
                inputName={'customId'}
                label={'Vlastní označení'}
                formName={'module-events'}
                sequenceKey={'sequenceEventCustomId'}
                showButton={true}
              />
            ) : (
              <InputField component={Input} id={'customId'} name={'customId'} label={'Vlastní označení'} />
            )}
          </Col>
          <Col md={12}>
            <InputField
              component={Input}
              id={'recordSymbol'}
              name={'recordSymbol'}
              label={'Spisová značka'}
              disabled={true}
            />
          </Col>
        </Row>
      </Col>
      <Col md={3} className="with-right-divider">
        <Row>
          <Col md={12}>
            <h4>Časy</h4>
          </Col>
        </Row>
        <Row key={'report.datetime'}>
          <Col md={12}>
            <DateField
              component={Date}
              dateOnly={false}
              id={'report.datetime'}
              name={'report.datetime'}
              label={'Čas oznámení *'}
              validate={required}
              disabled={disabled}
              onChange={value => change('eventDate', formatDateTimeString(value))}
            />
          </Col>
        </Row>
        <Row key={'eventDate'}>
          <Col md={12}>
            <DateField
              component={Date}
              dateOnly={false}
              id={'eventDate'}
              name={'eventDate'}
              label={'Čas události *'}
              validate={required}
              disabled={disabled}
            />
          </Col>
        </Row>
      </Col>
      <Col md={3} className="with-right-divider">
        <Row>
          <Col md={12}>
            <h4>Typ</h4>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <RemoteAutoCompleteTreeField
              component={RemoteAutoCompleteTree}
              id={'type'}
              change={change}
              formName={'module-events'}
              name={'type'}
              itemLabel={'name'}
              disabled={disabled}
              url={`/metropolis/api/ed/types`}
              fullWidth={true}
              labelFunction={o => get(o, 'name')}
              valueFunction={o => ({
                id: get(o, 'id')
              })}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Row>
              <Col md={12}>
                <h4>Stav</h4>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <AutoCompleteField
              component={AutoComplete}
              id="state"
              name="state"
              label="Stav *"
              items={stateEnum}
              fullWidth={true}
              labelFunction={o => get(o, 'label')}
              valueFunction={o => get(o, 'id')}
              validate={[required]}
              disabled={disabled}
            />
          </Col>
        </Row>
      </Col>
      <Col md={3} className="with-right-divider">
        <Row>
          <Col md={12}>
            <h4>Způsob hlášení</h4>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <RemoteAutoCompleteField
              id="report.type"
              name="report.type"
              label="Způsob hlášení"
              component={RemoteAutoComplete}
              url="/metropolis/api/ed/reportTypes"
              filterActive={true}
              labelFunction={o => get(o, 'name')}
              disabled={disabled}
              arrowstyle={{
                position: 'absolute',
                right: 0,
                top: 36
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <h4>Způsob řešení</h4>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <RemoteAutoCompleteField
              id="resolution"
              name="resolution"
              label="Způsob řešení"
              component={RemoteAutoComplete}
              url="/metropolis/api/ed/resolutions"
              filterActive={true}
              labelFunction={o => get(o, 'name')}
              disabled={disabled}
              arrowstyle={{
                position: 'absolute',
                right: 0,
                top: 36
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>

    <Row>
      <Col className="grid-system-margin" />
    </Row>

    <Row>
      <Col md={12}>
        <Divider />
      </Col>
    </Row>

    <Row>
      <Col className="grid-system-margin" />
    </Row>

    <Row>
      <Col md={12}>
        <Row>
          <Col md={6}>
            <h4>Adresa</h4>
          </Col>
          <Col>
            <h4>Vlastní místo</h4>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <AddressField component={Address} name={'address'} disabled={disabled} defaultPlace={eventDefaultPlace} />
          </Col>
          <Col md={6}>
            <RemoteAutoCompleteField
              name={'place'}
              url="/metropolis/api/address/place"
              fullWidth={true}
              labelFunction={o => get(o, 'name')}
              valueFunction={o => o}
              component={RemoteAutoComplete}
            />
          </Col>
        </Row>
      </Col>
    </Row>

    <Row>
      <Col className="grid-system-margin" />
    </Row>

    <Row>
      <Col md={12}>
        <Divider />
      </Col>
    </Row>

    <Row>
      <Col className="grid-system-margin" />
    </Row>

    <Row>
      <Col md={12}>
        <Row>
          <Col md={12}>
            <h4>Klasifikace</h4>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <RemoteChipInputField
              component={RemoteChipInput}
              id="classifications"
              name="classifications"
              label=""
              url="/metropolis/api/ed/classifications"
              filterActive={true}
              labelFunction={o => get(o, 'name')}
              fullWidth={true}
            />
            <InlineGridField
              component={InlineGrid}
              name="classifications"
              columns={eventsFormClassificationsGridColumns}
              showCheckboxes={false}
              deleting={false}
              adding={false}
              editing={false}
              filtering={false}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  </Grid>
);

export const EventsFormBasic = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/events/EventsFormBasic.tsx